import React, { useEffect } from 'react';
import { Download, Copy, Share2, Eye, FileText } from 'lucide-react';

const GeneratedResults = ({ templateData, generatePDF }) => {
  // Initialize MathJax when component mounts
  useEffect(() => {
    if (window.MathJax) {
      window.MathJax.typesetPromise();
    }
  }, [templateData]);

  const handleCopyToClipboard = () => {
    const content = generateTextContent();
    navigator.clipboard.writeText(content).then(() => {
      alert('內容已複製到剪貼板！');
    });
  };

  const generateTextContent = () => {
    const sections = [];
    
    if (templateData.problem) {
      sections.push(`問題: ${templateData.problem}`);
    }
    
    if (templateData.given) {
      sections.push(`已知: ${templateData.given}`);
    }
    
    if (templateData.toProve) {
      sections.push(`求證: ${templateData.toProve}`);
    }
    
    if (templateData.proof) {
      sections.push(`證明/解答:\n${templateData.proof}`);
    }
    
    if (templateData.conclusion) {
      sections.push(`結論: ${templateData.conclusion}`);
    }
    
    if (templateData.formulas && templateData.formulas.length > 0) {
      sections.push(`相關公式:\n${templateData.formulas.join('\n')}`);
    }
    
    return sections.join('\n\n');
  };

  const isEmpty = !templateData.problem && !templateData.given && !templateData.toProve && 
                  !templateData.proof && !templateData.conclusion;

  if (isEmpty) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-600 mb-4">沒有生成的內容</h2>
          <p className="text-gray-500 mb-6">
            請先使用模板精靈創建數學內容，然後回到這裡查看結果。
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
          >
            重新載入
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">生成結果</h1>
        <p className="text-gray-600">您的數學內容已生成完成</p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap justify-center gap-4 mb-8">
        <button
          onClick={generatePDF}
          className="flex items-center space-x-2 bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors shadow-md"
        >
          <Download className="w-5 h-5" />
          <span>下載PDF</span>
        </button>
        
        <button
          onClick={handleCopyToClipboard}
          className="flex items-center space-x-2 bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 transition-colors shadow-md"
        >
          <Copy className="w-5 h-5" />
          <span>複製內容</span>
        </button>
        
        <button
          onClick={() => window.print()}
          className="flex items-center space-x-2 bg-purple-500 text-white px-6 py-3 rounded-lg hover:bg-purple-600 transition-colors shadow-md"
        >
          <Eye className="w-5 h-5" />
          <span>預覽列印</span>
        </button>
      </div>

      {/* Content Preview */}
      <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
        <div className="max-w-none prose prose-lg">
          {/* Problem Section */}
          {templateData.problem && (
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm mr-3">1</span>
                問題
              </h3>
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-gray-700 math-content">{templateData.problem}</p>
              </div>
            </div>
          )}

          {/* Given Section */}
          {templateData.given && (
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm mr-3">2</span>
                已知條件
              </h3>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-gray-700 math-content">{templateData.given}</p>
              </div>
            </div>
          )}

          {/* To Prove Section */}
          {templateData.toProve && (
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                <span className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center text-sm mr-3">3</span>
                求證
              </h3>
              <div className="p-4 bg-yellow-50 rounded-lg">
                <p className="text-gray-700 math-content">{templateData.toProve}</p>
              </div>
            </div>
          )}

          {/* Proof/Solution Section */}
          {templateData.proof && (
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm mr-3">4</span>
                證明/解答
              </h3>
              <div className="p-4 bg-purple-50 rounded-lg">
                <div className="text-gray-700 math-content whitespace-pre-wrap">{templateData.proof}</div>
              </div>
            </div>
          )}

          {/* Conclusion Section */}
          {templateData.conclusion && (
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                <span className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm mr-3">5</span>
                結論
              </h3>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-gray-700 math-content">{templateData.conclusion}</p>
              </div>
            </div>
          )}

          {/* Formulas Section */}
          {templateData.formulas && templateData.formulas.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                <span className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center text-sm mr-3">📐</span>
                相關公式
              </h3>
              <div className="p-4 bg-indigo-50 rounded-lg">
                <div className="space-y-3">
                  {templateData.formulas.map((formula, index) => (
                    <div key={index} className="p-3 bg-white rounded border-l-4 border-indigo-400">
                      <div className="math-content text-center">{`$$${formula}$$`}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-gray-500 text-sm">
        <p>由 RightMaths 數學右腦 生成 • {new Date().toLocaleDateString('zh-TW')}</p>
      </div>
    </div>
  );
};

export default GeneratedResults; 